import React from "react";

import Collapse from "@material-ui/core/Collapse";
import IconButton from "@material-ui/core/IconButton";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import PropTypes from "prop-types";

import RbCheckbox from "components/RbCheckbox";
import RbFlexTableContext from "components/RbFlexTable/RbFlexTableContext";
import RbFlexTableHeadContext from "components/RbFlexTableHead/RbFlexTableHeadContext";
import useToggleControlled from "hooks/useToggleControlled";

import useStyles from "./RbFlexTableRow.styles";

const CollapseRow = ({ columnsCount, children, ...other }) => {
  return (
    <div {...other}>
      <div role="cell" aria-colspan={columnsCount}>
        {children}
      </div>
    </div>
  );
};

CollapseRow.propTypes = {
  children: PropTypes.node.isRequired,
  columnsCount: PropTypes.number.isRequired,
};

const RbFlexTableRow = React.forwardRef(function RbFlexTableRow(props, ref) {
  const {
    children,
    expanded: expandedProp,
    index,
    onExpandChange: onExpandChangeProp,
    onExpandClose,
    onlyExpand,
    onSelectChange: onSelectChangeProp,
    renderExpand,
    selected: selectedProp,
    ...other
  } = props;

  const [expanded, onExpandChange] = useToggleControlled(
    expandedProp,
    onExpandChangeProp
  );

  const [selected, onSelectChange] = useToggleControlled(
    selectedProp,
    onSelectChangeProp
  );

  const { variant, expandable, id, hoverable, selectable } = React.useContext(
    RbFlexTableContext
  );

  const headContext = React.useContext(RbFlexTableHeadContext);

  const classes = useStyles(props);

  const handleSelectChange = React.useCallback(
    (event) => {
      event.stopPropagation();
      if (onSelectChange) {
        onSelectChange(event, index);
      }
    },
    [onSelectChange, index]
  );

  const handleExpandChange = React.useCallback(
    (event) => {
      event.stopPropagation();
      if (onExpandChange) {
        onExpandChange(event, index);
      }
    },
    [onExpandChange, index]
  );

  const handleExited = React.useCallback(() => {
    if (onExpandClose) {
      onExpandClose();
    }
  }, [onExpandClose]);

  const rowId = id ? `${id}-row-${index}` : undefined;
  const expandId = id ? `${id}-row-${index}-expanded` : undefined;
  const oddOrEven = index % 2 === 0 ? "even" : "odd";

  const className = `
    ${classes.root}
    ${headContext?.head ? classes.root_head : classes.root_body}
    ${classes[`root_${variant}`]}
    ${classes[`root_${oddOrEven}`]}
    ${expanded ? classes.root_expanded : ""}
    ${selected ? classes.root_selected : ""}
    ${hoverable ? classes.root_hoverable : ""}
  `;

  const expandClassName = `
  ${classes.expand}
  ${classes[`expand_${variant}`]}
  ${classes[`expand_${oddOrEven}`]}
`;

  let columnsCount = React.Children.toArray(children).length;
  columnsCount = selectable ? columnsCount + 1 : columnsCount;
  columnsCount = expandable ? columnsCount + 1 : columnsCount;

  const renderSelectCell = () => {
    if (!selectable) {
      return null;
    }
    return (
      <div role="cell" className={classes.selectCell}>
        {!headContext?.head && (
          <RbCheckbox checked={selected} onChange={handleSelectChange} />
        )}
      </div>
    );
  };

  const renderExpandCell = () => {
    if (!expandable) {
      return null;
    }
    return (
      <div role="cell" className={classes.expandCell}>
        {!headContext?.head && (
          <IconButton
            className={`${classes.expandIcon} ${
              expanded ? classes.expandIcon_expanded : ""
            }`}
            onClick={handleExpandChange}
            size="small"
            aria-expanded={expanded}
            aria-controls={expandId}
          >
            <KeyboardArrowDownIcon />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <>
      {!onlyExpand && (
        <div id={rowId} role="row" {...other} ref={ref} className={className}>
          {renderSelectCell()}
          {children}
          {renderExpandCell()}
        </div>
      )}
      <Collapse
        className={expandClassName}
        columnsCount={columnsCount}
        component={CollapseRow}
        id={expandId}
        in={expanded}
        role="row"
        timeout="auto"
        unmountOnExit
        onExited={handleExited}
      >
        {renderExpand && renderExpand(index)}
      </Collapse>
    </>
  );
});

RbFlexTableRow.propTypes = {
  children: PropTypes.node,
  expanded: PropTypes.bool,
  index: PropTypes.number,
  onExpandChange: PropTypes.func,
  onExpandClose: PropTypes.func,
  onlyExpand: PropTypes.bool,
  onSelectChange: PropTypes.func,
  renderExpand: PropTypes.func,
  selected: PropTypes.bool,
};

RbFlexTableRow.defaultProps = {
  children: null,
  expanded: undefined,
  index: undefined,
  onExpandChange: undefined,
  onExpandClose: undefined,
  onlyExpand: false,
  onSelectChange: undefined,
  renderExpand: undefined,
  selected: undefined,
};

export default React.memo(RbFlexTableRow);
